import React, { useEffect, useMemo, useState } from 'react';
import { X, IndianRupee, Loader2, AlertCircle, Info } from 'lucide-react';
import styles from '../Vendors.module.css';
import { vendorFetch, errText, inr, inr2, fmtDate, ymd, r2, MODES, CLEARS_AT_ONCE } from '../../../utils/vendorApi';

/** Pay a vendor: spread over open bills (oldest first or chosen); the rest is kept as advance */
export default function PaymentModal({ storeId, vendorId: presetVendor = '', billId: presetBill = '', onClose, onSaved }) {
    const [vendors, setVendors] = useState([]);
    const [vendorId, setVendorId] = useState(presetVendor);
    const [bills, setBills] = useState([]);
    const [loadingBills, setLoadingBills] = useState(false);
    const [amount, setAmount] = useState('');
    const [mode, setMode] = useState('CASH');
    const [cleared, setCleared] = useState(true);
    const [reference, setReference] = useState('');
    const [date, setDate] = useState(ymd());
    const [note, setNote] = useState('');
    const [how, setHow] = useState(presetBill ? 'PICK' : 'AUTO');
    const [picked, setPicked] = useState({});   // billId -> amount string
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { vendorFetch('', { storeId }).then((j) => setVendors(j.data || [])).catch((e) => setError(errText(e))); }, [storeId]);
    useEffect(() => {
        if (!vendorId) { setBills([]); return; }
        setLoadingBills(true);
        vendorFetch(`/bills?vendorId=${vendorId}&status=OPEN`, { storeId })
            .then((j) => {
                const list = (j.data || []).sort((a, b) => new Date(a.dueDate || a.billDate) - new Date(b.dueDate || b.billDate));
                setBills(list);
                if (presetBill) {
                    const b = list.find((x) => String(x._id) === String(presetBill));
                    if (b) { setPicked({ [b._id]: String(b.balance) }); setAmount(String(b.balance)); }
                }
            })
            .catch((e) => setError(errText(e)))
            .finally(() => setLoadingBills(false));
    }, [vendorId, storeId, presetBill]);
    useEffect(() => { setCleared(CLEARS_AT_ONCE.includes(mode)); }, [mode]);

    const vendor = vendors.find((v) => String(v._id) === String(vendorId));
    const owed = bills.reduce((s, b) => s + b.balance, 0);
    const amt = Number(amount) || 0;
    const plan = useMemo(() => {
        if (how === 'PICK') {
            const used = r2(Object.values(picked).reduce((s, v) => s + (Number(v) || 0), 0));
            return { used, advance: r2(Math.max(0, amt - used)), over: used > amt + 0.01 };
        }
        let left = amt; const per = {};
        bills.forEach((b) => { const t = Math.min(left, b.balance); if (t > 0) { per[b._id] = r2(t); left = r2(left - t); } });
        return { used: r2(amt - left), advance: r2(left), per, over: false };
    }, [how, picked, amt, bills]);

    const togglePick = (b) => setPicked((p) => {
        const n = { ...p };
        if (n[b._id] !== undefined) delete n[b._id];
        else n[b._id] = String(r2(Math.min(b.balance, Math.max(0, amt - Object.values(p).reduce((s, v) => s + (Number(v) || 0), 0))) || b.balance));
        return n;
    });

    const save = async () => {
        setError('');
        if (!vendorId) { setError('Choose the vendor.'); return; }
        if (amt <= 0) { setError('Enter the amount paid.'); return; }
        if (plan.over) { setError('The bills chosen add up to more than the payment.'); return; }
        const over = how === 'PICK' && bills.find((b) => (Number(picked[b._id]) || 0) > b.balance + 0.01);
        if (over) { setError(`Bill ${over.billNo || over.refNo} has only ${inr2(over.balance)} left.`); return; }
        if (plan.advance > 0.01 && !window.confirm(`${inr(plan.advance)} is more than the bills chosen. Keep it as advance with ${vendor?.name}?`)) return;
        setSaving(true);
        try {
            const json = await vendorFetch('/payments', {
                method: 'POST', storeId,
                body: {
                    vendorId, amount: amt, mode, reference, date, note, clearStatus: cleared ? 'CLEARED' : 'PENDING',
                    ...(how === 'PICK' ? { allocations: Object.entries(picked).filter(([, v]) => Number(v) > 0).map(([billId, v]) => ({ billId, amount: Number(v) })) } : {}),
                },
            });
            onSaved?.(json.data, json.message);
        } catch (e) { setError(errText(e)); } finally { setSaving(false); }
    };

    return (
        <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()} role="presentation">
            <div className={styles.modal} style={{ width: 'min(680px, 100%)' }} role="dialog" aria-modal="true" aria-label="Pay vendor">
                <div className={styles.modalHead}>
                    <span className={styles.modalIcon}><IndianRupee size={18} /></span>
                    <div><h3>Pay vendor</h3><p>Shows in Expenses; cash is taken from the drawer</p></div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close"><X size={17} /></button>
                </div>
                <div className={styles.modalBody}>
                    {error && <div className={`${styles.msg} ${styles.msgErr}`}><AlertCircle size={14} /><span>{error}</span></div>}
                    <div className={styles.grid2}>
                        <label className={styles.field}><span>Vendor <em>*</em></span>
                            <select className={styles.input} value={vendorId} onChange={(e) => { setVendorId(e.target.value); setPicked({}); }} autoFocus={!presetVendor}>
                                <option value="">Choose vendor</option>
                                {vendors.filter((v) => v.isActive !== false || String(v._id) === String(vendorId)).map((v) => <option key={v._id} value={v._id}>{v.name}{v.summary?.balance > 0.009 ? ` (owe ${inr(v.summary.balance)})` : ''}</option>)}
                            </select>
                        </label>
                        <label className={styles.field}><span>Amount paid (₹) <em>*</em></span>
                            <input className={styles.input} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" autoFocus={Boolean(presetVendor)} />
                            {owed > 0 && <small>Open bills: {inr2(owed)} · <button type="button" className={styles.btnGhost} style={{ height: 'auto', padding: 0 }} onClick={() => setAmount(String(r2(owed)))}>pay all</button></small>}
                        </label>
                        <label className={styles.field}><span>Paid by</span>
                            <select className={styles.input} value={mode} onChange={(e) => setMode(e.target.value)}>{MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select>
                        </label>
                        <label className={styles.field}><span>Date</span><input className={styles.input} type="date" value={date} max={ymd()} onChange={(e) => setDate(e.target.value)} /></label>
                        <label className={styles.field}><span>Reference</span><input className={styles.input} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / cheque no. / note" maxLength={80} /></label>
                        <label className={styles.field}><span>Status</span>
                            <div className={styles.segment}>
                                <button type="button" className={cleared ? styles.segOn : ''} onClick={() => setCleared(true)}>Cleared</button>
                                <button type="button" className={!cleared ? styles.segOn : ''} onClick={() => setCleared(false)}>Not cleared yet</button>
                            </div>
                            <small>{cleared ? 'Money has left your account / drawer.' : 'For a cheque or transfer still in process. Mark it cleared later from Vendors or Expenses.'}</small>
                        </label>
                    </div>

                    {vendorId && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span className={styles.sectionLabel}>Put this payment on</span>
                                <div className={styles.segment}>
                                    <button type="button" className={how === 'AUTO' ? styles.segOn : ''} onClick={() => setHow('AUTO')}>Oldest bills first</button>
                                    <button type="button" className={how === 'PICK' ? styles.segOn : ''} onClick={() => setHow('PICK')}>Choose bills</button>
                                </div>
                            </div>
                            {loadingBills ? <div className={styles.muted}><Loader2 size={13} className={styles.spin} /> Loading bills...</div>
                                : bills.length === 0 ? <div className={styles.note}><Info size={14} /> <span>No open bills. The whole amount is kept as advance and used on the next bill.</span></div>
                                    : (
                                        <div className={styles.allocList}>
                                            {bills.map((b) => {
                                                const on = how === 'PICK' ? picked[b._id] !== undefined : Boolean(plan.per?.[b._id]);
                                                return (
                                                    <label key={b._id} className={`${styles.alloc} ${on ? styles.allocOn : ''}`}>
                                                        <input type="checkbox" checked={on} disabled={how === 'AUTO'} onChange={() => togglePick(b)} />
                                                        <span className={styles.stack}><b>{b.billNo || b.refNo}</b><small>{fmtDate(b.billDate)} · due {fmtDate(b.dueDate)}{b.overdue ? ' · overdue' : ''}</small></span>
                                                        <span className={`${styles.num} ${b.overdue ? styles.red : ''}`}>{inr2(b.balance)} left</span>
                                                        {how === 'PICK'
                                                            ? <input className={styles.input} type="number" min="0" step="0.01" value={picked[b._id] ?? ''} disabled={!on} onChange={(e) => setPicked((p) => ({ ...p, [b._id]: e.target.value }))} aria-label="Amount for this bill" />
                                                            : <span className={`${styles.num} ${styles.green}`} style={{ textAlign: 'right' }}>{plan.per?.[b._id] ? inr2(plan.per[b._id]) : '—'}</span>}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                            {amt > 0 && (
                                <div className={styles.note}>
                                    <Info size={14} style={{ flexShrink: 0 }} />
                                    <span>On bills: <b>{inr2(plan.used)}</b>{plan.advance > 0.01 && <> · Kept as advance: <b>{inr2(plan.advance)}</b></>}{plan.over && <b className={styles.red}> · Bills chosen are more than the payment</b>}</span>
                                </div>
                            )}
                        </div>
                    )}
                    <label className={styles.field}><span>Note</span><input className={styles.input} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Optional" /></label>
                </div>
                <div className={styles.modalFoot}>
                    <div className={styles.footRight}>
                        <button type="button" className={styles.btn} onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="button" className={styles.btnPrimary} onClick={save} disabled={saving}>{saving && <Loader2 size={14} className={styles.spin} />} Save payment</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
